import React, { useEffect, useState } from 'react';

const API = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;

/**
 * Cascading Country → Utility → Tariff picker for the `grid_tariff_ref` field
 * type. Stores the selected tariff's numeric id as a string on the node's
 * properties object, so it survives tariff renames.
 */
const GridTariffRefField = ({ value, onChange, direction = 'import' }) => {
  const [countries, setCountries] = useState([]);
  const [utilities, setUtilities] = useState([]);
  const [tariffs, setTariffs] = useState([]);

  const [country, setCountry] = useState('');
  const [utilityId, setUtilityId] = useState('');
  const [tariffId, setTariffId] = useState(value || '');

  // Load countries once
  useEffect(() => {
    fetch(`${API}/api/countries`)
      .then((r) => r.json())
      .then((list) => setCountries(list || []))
      .catch(() => {});
  }, []);

  // If we're given a pre-existing tariff id, resolve back through utility/country
  // so the cascade shows the correct breadcrumb.
  useEffect(() => {
    if (!value || country) return;
    fetch(`${API}/api/grid-tariffs/${value}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && d.tariff) {
          setCountry(d.tariff.country_code);
          setUtilityId(String(d.tariff.utility_id));
          setTariffId(String(d.tariff.id));
        }
      })
      .catch(() => {});
  }, [value, country]);

  // Load utilities whenever country changes
  useEffect(() => {
    if (!country) {
      setUtilities([]);
      return;
    }
    fetch(`${API}/api/utilities?country=${country}`)
      .then((r) => r.json())
      .then((list) => setUtilities(list || []))
      .catch(() => {});
  }, [country]);

  // Load tariffs whenever utility changes
  useEffect(() => {
    if (!country || !utilityId) {
      setTariffs([]);
      return;
    }
    fetch(`${API}/api/grid-tariffs?country=${country}&utility_id=${utilityId}&direction=${direction}`)
      .then((r) => r.json())
      .then((list) => setTariffs(list || []))
      .catch(() => {});
  }, [country, utilityId, direction]);

  const handleCountry = (e) => {
    setCountry(e.target.value);
    setUtilityId('');
    setTariffId('');
    onChange('', null);
  };

  const handleUtility = (e) => {
    setUtilityId(e.target.value);
    setTariffId('');
    onChange('', null);
  };

  const handleTariff = (e) => {
    const v = e.target.value;
    setTariffId(v);
    const meta = tariffs.find((t) => String(t.id) === String(v)) || null;
    onChange(v, meta);
  };

  const selected = tariffs.find((t) => String(t.id) === String(tariffId));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <select value={country} onChange={handleCountry} style={{ width: '100%' }}>
        <option value="">— Country —</option>
        {countries.map((c) => (
          <option key={c.code} value={c.code}>{c.name} ({c.code})</option>
        ))}
      </select>

      <select value={utilityId} onChange={handleUtility} disabled={!country} style={{ width: '100%' }}>
        <option value="">— Utility —</option>
        {utilities.map((u) => (
          <option key={u.id} value={u.id}>{u.name}</option>
        ))}
      </select>

      <select value={tariffId} onChange={handleTariff} disabled={!utilityId} style={{ width: '100%' }}>
        <option value="">— Tariff —</option>
        {tariffs.map((t) => (
          <option key={t.id} value={t.id}>{t.name}</option>
        ))}
      </select>

      {selected && (
        <div style={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic' }}>
          {selected.utility_code} · {selected.code} · valid {selected.valid_from} → {selected.valid_to}
        </div>
      )}
    </div>
  );
};

export default GridTariffRefField;
